import { FC } from 'react';

import dashboardMultiActionsButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/common/dashboard_multi_actions_button_click';
import { VSpacing } from '@hh.ru/magritte-ui';

import Conversion from 'bloko/blocks/conversion';
import { HoverTip, Tip } from 'bloko/blocks/drop';
import { TipPlacement } from 'bloko/blocks/drop/Tip';
import Icon, { IconColor, IconKind, IconScale, IconView } from 'bloko/blocks/icon';
import Link, { LinkIconPosition } from 'bloko/blocks/link';
import Text from 'bloko/blocks/text';
import { formatToReactComponent } from 'bloko/common/trl';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';

import BudgetIncreaseModal from 'components/Dashboard/MultiActions/BudgetIncrease/BudgetIncreaseModal/BudgetIncreaseModal';

import styles from 'components/Dashboard/MultiActions/BudgetIncrease/BudgetIncrease.module.less';

const TrlKeys = {
    budgetIncrease: 'Dashboard.MultiActions.budgetIncrease',
    budgetIncreaseInfoTitle: 'Dashboard.MultiActions.budgetIncrease.info.title',
    budgetIncreaseInfoText1: 'Dashboard.MultiActions.budgetIncrease.info.text.infinityBudget',
    budgetIncreaseInfoText2: 'Dashboard.MultiActions.budgetIncrease.info.text.stoppedByEndDate',
    campaignsCount: {
        zero: `Dashboard.MultiActions.budgetIncrease.campaign.zero`,
        one: `Dashboard.MultiActions.budgetIncrease.campaign.one`,
        some: `Dashboard.MultiActions.budgetIncrease.campaign.some`,
        many: `Dashboard.MultiActions.budgetIncrease.campaign.many`,
    },
};

interface BudgetIncreaseProps {
    availableCampaignIdsForBudget: Record<number, boolean>;
    campaignsForBudgetIncreaseCount: number;
    infinityBudgetCampaignsCount: number;
    stoppedByEndDateCampaignsCount: number;
}

const BudgetIncrease: FC<BudgetIncreaseProps> = ({
    availableCampaignIdsForBudget,
    campaignsForBudgetIncreaseCount,
    infinityBudgetCampaignsCount,
    stoppedByEndDateCampaignsCount,
}) => {
    const trl = useTrls();

    const [isBudgetModalVisible, setIsBudgetModalVisibleOn, setIsBudgetModalVisibleOff] = useOnOffState(false);

    const budgetIncreaseOnClick = () => {
        setIsBudgetModalVisibleOn();
        dashboardMultiActionsButtonClick({
            buttonName: 'dashboard_multi_actions_increase_budget',
            checkedCampaignsCount: campaignsForBudgetIncreaseCount,
            campaignIds: JSON.stringify(Object.keys(availableCampaignIdsForBudget).map(Number)),
        });
    };

    return (
        <>
            <div className={styles.budget}>
                <Link
                    onClick={budgetIncreaseOnClick}
                    disabled={false}
                    icon={
                        <Icon
                            highlighted={IconColor.Blue70}
                            initial={IconColor.Blue50}
                            view={IconView.Arrows}
                            kind={IconKind.DiagonalOut}
                            scale={IconScale.Small}
                        />
                    }
                    iconPosition={LinkIconPosition.Left}
                    data-qa="campaign-budget-increase"
                >
                    {trl(TrlKeys.budgetIncrease, [campaignsForBudgetIncreaseCount])}
                </Link>
                {!!(infinityBudgetCampaignsCount || stoppedByEndDateCampaignsCount) && (
                    <HoverTip
                        placement={TipPlacement.Bottom}
                        render={() => (
                            <>
                                <Text>{trl(TrlKeys.budgetIncreaseInfoTitle)}</Text>
                                <VSpacing default={8} />
                                <ul className={styles.list}>
                                    {infinityBudgetCampaignsCount > 0 && (
                                        <li className={styles.item}>
                                            {formatToReactComponent(trl(TrlKeys.budgetIncreaseInfoText1), {
                                                '[0]': (
                                                    <Conversion
                                                        zero={trl(TrlKeys.campaignsCount.zero)}
                                                        one={trl(TrlKeys.campaignsCount.one)}
                                                        some={trl(TrlKeys.campaignsCount.some)}
                                                        many={trl(TrlKeys.campaignsCount.many)}
                                                        value={infinityBudgetCampaignsCount}
                                                        hasValue={true}
                                                    />
                                                ),
                                            })}
                                        </li>
                                    )}
                                    {stoppedByEndDateCampaignsCount > 0 && (
                                        <li className={styles.item}>
                                            {formatToReactComponent(trl(TrlKeys.budgetIncreaseInfoText2), {
                                                '[0]': (
                                                    <Conversion
                                                        zero={trl(TrlKeys.campaignsCount.zero)}
                                                        one={trl(TrlKeys.campaignsCount.one)}
                                                        some={trl(TrlKeys.campaignsCount.some)}
                                                        many={trl(TrlKeys.campaignsCount.many)}
                                                        value={stoppedByEndDateCampaignsCount}
                                                        hasValue={true}
                                                    />
                                                ),
                                            })}
                                        </li>
                                    )}
                                </ul>
                            </>
                        )}
                        DropElement={Tip}
                    >
                        <Icon
                            highlighted={IconColor.Gray60}
                            initial={IconColor.Gray50}
                            view={IconView.Info}
                            scale={IconScale.Small}
                        />
                    </HoverTip>
                )}
            </div>
            {isBudgetModalVisible && (
                <BudgetIncreaseModal
                    isVisible={isBudgetModalVisible}
                    hideModal={setIsBudgetModalVisibleOff}
                    availableCampaignIdsForBudget={availableCampaignIdsForBudget}
                    campaignsForBudgetIncreaseCount={campaignsForBudgetIncreaseCount}
                />
            )}
        </>
    );
};

export default BudgetIncrease;
